import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ItemFormSurface } from "../EditDrawer";
import LinkRoomsSection from "./LinkRoomsSection";
import { refusalCopy } from "./linkRefusalCopy";
import { describeError, useToast } from "../toast";
import { PermissionGate } from "../../auth/access";
import { useFormat } from "../../i18n/format";
import { translateKey } from "../../i18n/translate";

/**
 * The Connections panel (`INT-11`). Doctrine: `docs/property-link.md` §6.
 *
 * ## What it replaces, and why the shape matters
 *
 * The old PMS card offered a HARDCODED list of five vendors — Mews, Opera,
 * Protel, Apaleo, Cloudbeds — while the folio seam knew only three. Selecting
 * Protel stored a free string no seam could act on and produced **no refusal at
 * all**, which is exactly the failure `folioPostingProviders.ts` exists to
 * prevent: somebody selects a vendor and assumes charges are flowing.
 *
 * So this component has **no vendor list of its own.** Vendors, their config
 * fields, their credential fields and their capability chips all come from
 * `GET /link/adapters`, which serves the adapter registry. Adding an adapter is
 * one server file and never touches this one — that is the epic's success
 * criterion, and this component is half of what makes it true.
 *
 * ## It never claims more than it can prove
 *
 * "Test connection" performs a REAL read against the vendor and reports the
 * result by code. A green tick the provider has not earned would contradict this
 * codebase's own conventions — it reports `cleared:false` rather than claim an
 * unproven wipe, and returns `undefined` rather than a placeholder guest name.
 *
 * Credentials are write-only. A stored one shows field names and a masked hint
 * and can be replaced, never read back; there is no server route that would
 * return one.
 */

interface Capabilities {
  postCharge: boolean;
  reverseCharge: boolean;
  readStays: boolean;
  readMenu: boolean;
  injectOrder: boolean;
  authoriseCharge: boolean;
  postingKey: "reservation" | "room";
  chargeableFlag: boolean;
  staysResolveOnly?: boolean;
  demo: boolean;
}

interface FieldSpec {
  key: string;
  label: string;
  secret?: boolean;
  required?: boolean;
  placeholder?: string;
  help?: string;
  /** `outlets` = picked from the vendor's own list once a credential is saved. */
  kind?: "text" | "outlets";
}

interface AdapterStatus {
  vendor: string;
  displayName: string;
  capabilities: Capabilities;
  operational: boolean;
  listsUnits?: boolean;
  reason: string | null;
  configFields?: FieldSpec[];
  credentialFields?: FieldSpec[];
}

interface Connection {
  id: string;
  vendor: string;
  displayName: string;
  scope: "premise" | "company" | "global";
  enabled: boolean;
  adapterKnown: boolean;
  capabilities: Capabilities | null;
  config: Record<string, string>;
  hasCredentials: boolean;
  credential: { fields: string[]; hints?: Record<string, string> } | null;
  lastOkAt: string | null;
  lastErrorCode: string | null;
  lastErrorAt: string | null;
}

interface Props {
  premiseId: string;
}

export default function LinkConnectionsPanel({ premiseId }: Props) {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const refusal = useCallback(
    (code: string | null) => refusalCopy(t, code),
    [t]
  );
  const [adapters, setAdapters] = useState<AdapterStatus[] | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [vendor, setVendor] = useState("");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The "Add integration" modal; its own refusal shows inside it. */
  const [adding, setAdding] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([
        fetch("/api/cms/v1/link/adapters", { credentials: "include" }).then(
          (r) => r.json()
        ),
        fetch(`/api/cms/v1/premise/${premiseId}/link/connections`, {
          credentials: "include",
        }).then((r) => r.json()),
      ]);
      setAdapters(a.adapters ?? []);
      setConnections(c.connections ?? []);
    } catch {
      setError(t("linkConnectionsPanel.loadFailed"));
      setAdapters([]);
    }
  }, [premiseId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const chosen = adapters?.find((a) => a.vendor === vendor) ?? null;

  /** Resolves true on success, so the modal can close itself. */
  async function save(): Promise<boolean> {
    setBusy(true);
    setSaveError(null);
    try {
      const res = await fetch(
        `/api/cms/v1/premise/${premiseId}/link/connections`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ vendor, config, credentials }),
        }
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        const why =
          refusal(body.error ?? null) || t("linkConnectionsPanel.saveFailed");
        setSaveError(why);
        toast.error(
          t("common:couldNotBecause", {
            action: t("common:actions.saveIntegration", {
              name: chosen?.displayName ?? vendor,
            }),
            reason: why.replace(/[.\s]+$/, ""),
          })
        );
        return false;
      }
      setCredentials({});
      toast.success(
        t("linkConnectionsPanel.saved", {
          name: chosen?.displayName ?? vendor,
        })
      );
      await load();
      return true;
    } catch (e) {
      setSaveError(t("linkConnectionsPanel.serverUnreachable"));
      toast.error(
        describeError(t, "saveIntegration", e, {
          name: chosen?.displayName ?? vendor,
        })
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function test(id: string) {
    setBusy(true);
    setTestResult(null);
    try {
      const res = await fetch(
        `/api/cms/v1/premise/${premiseId}/link/connections/${id}/test`,
        { method: "POST", credentials: "include" }
      );
      const body = (await res.json()) as {
        ok: boolean;
        code: string | null;
        property?: {
          name: string;
          timezone: string | null;
          currency: string | null;
        } | null;
      };
      const name =
        connections.find((c) => c.id === id)?.displayName ??
        t("linkConnectionsPanel.theIntegration");
      if (body.ok) {
        // Name the property the credential opens: a token for the WRONG hotel
        // connects perfectly, and this is where an operator can catch it.
        const details = body.property
          ? [body.property.timezone, body.property.currency]
              .filter(Boolean)
              .join(", ")
          : "";
        const variant = !body.property
          ? "plain"
          : details
            ? "toPropertyWithDetails"
            : "toProperty";
        const params = { name, property: body.property?.name, details };
        setTestResult(
          translateKey(
            t,
            `premises:linkConnectionsPanel.testResult.${variant}`,
            params
          )
        );
        toast.success(
          translateKey(
            t,
            `premises:linkConnectionsPanel.connected.${variant}`,
            params
          )
        );
      } else {
        const why = refusal(body.code) || t("linkConnectionsPanel.testFailed");
        setTestResult(why);
        toast.error(
          t("linkConnectionsPanel.testFailedFor", {
            name,
            reason: why.replace(/[.\s]+$/, ""),
          })
        );
      }
      await load();
    } catch (e) {
      toast.error(describeError(t, "testConnection", e));
    } finally {
      setBusy(false);
    }
  }

  if (adapters === null) {
    return (
      <Card variant="outlined">
        <CardContent>
          <CircularProgress size={20} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1">
            {t("linkConnectionsPanel.title")}
          </Typography>

          {adapters.length === 0 && (
            <Alert severity="info" data-testid="link-no-adapters">
              {t("linkConnectionsPanel.noAdapters")}
            </Alert>
          )}

          {connections.map((c) => (
            <Box
              key={c.id}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
              data-testid={`link-connection-${c.vendor}`}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">{c.displayName}</Typography>
                  {c.scope !== "premise" && (
                    <Chip
                      size="small"
                      label={t("linkConnectionsPanel.fromChain")}
                    />
                  )}
                  {!c.enabled && (
                    <Chip size="small" label={t("common:labels.off")} />
                  )}
                  {c.capabilities?.demo && (
                    <Chip
                      size="small"
                      color="warning"
                      label={t("linkConnectionsPanel.demo")}
                    />
                  )}
                </Stack>

                {/* A connection bound to a vendor this build cannot talk to is
                    REPORTED, not hidden: it is exactly what an operator needs
                    to see, and hiding it is how the old page lied. */}
                {!c.adapterKnown && (
                  <Alert severity="error">
                    {t("linkConnectionsPanel.unknownAdapter", {
                      vendor: c.vendor,
                    })}
                  </Alert>
                )}

                {c.capabilities && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {c.capabilities.postCharge && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("linkConnectionsPanel.capabilities.charges")}
                      />
                    )}
                    {c.capabilities.readStays && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          c.capabilities.staysResolveOnly &&
                          c.config?.drivesOccupancy !== "on"
                            ? t(
                                "linkConnectionsPanel.capabilities.staysBilling"
                              )
                            : t("linkConnectionsPanel.capabilities.stays")
                        }
                      />
                    )}
                    {c.capabilities.readMenu && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("linkConnectionsPanel.capabilities.menu")}
                      />
                    )}
                    {/* Stated out loud rather than left to be assumed: a vendor
                        that cannot tell us whether a room may be charged means
                        the check is unavailable, not that it passed. */}
                    {!c.capabilities.chargeableFlag && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("linkConnectionsPanel.capabilities.noNoPost")}
                      />
                    )}
                  </Stack>
                )}

                {c.credential && (
                  <Typography variant="caption" color="text.secondary">
                    {t("linkConnectionsPanel.credentialsStored", {
                      fields:
                        c.credential.fields
                          .map((f) => `${f} ${c.credential?.hints?.[f] ?? ""}`)
                          .join(", ") ||
                        t("linkConnectionsPanel.credentialsUnreadable"),
                    })}
                  </Typography>
                )}

                {c.lastErrorCode && (
                  <Alert severity="warning">
                    {refusal(c.lastErrorCode)}
                    {c.lastErrorAt ? ` (${fmt.dateTime(c.lastErrorAt)})` : ""}
                  </Alert>
                )}

                <Box>
                  <PermissionGate
                    resource="pms-link"
                    level="administrate"
                    deniedProps={{ disabled: true }}
                  >
                    <Button
                      size="small"
                      disabled={busy || !c.adapterKnown}
                      onClick={() => void test(c.id)}
                      data-testid={`link-test-${c.vendor}`}
                    >
                      {t("linkConnectionsPanel.testConnection")}
                    </Button>
                  </PermissionGate>
                </Box>

                {c.adapterKnown &&
                  c.scope === "premise" &&
                  adapters.find((a) => a.vendor === c.vendor)?.listsUnits && (
                    <LinkRoomsSection
                      premiseId={premiseId}
                      vendor={c.vendor}
                      displayName={c.displayName}
                      hasCredentials={c.hasCredentials}
                      config={c.config ?? {}}
                      occupancyOptIn={Boolean(
                        c.capabilities?.readStays &&
                        c.capabilities?.staysResolveOnly
                      )}
                      onSaved={load}
                    />
                  )}
              </Stack>
            </Box>
          ))}

          {testResult && <Alert severity="info">{testResult}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          {adapters.length > 0 && (
            <PermissionGate resource="pms-link" level="administrate">
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    // Reset on open, not on close: clearing mid-fade would
                    // blank the form while it animates out.
                    setVendor("");
                    setConfig({});
                    setCredentials({});
                    setSaveError(null);
                    setAdding(true);
                  }}
                  data-testid="link-add"
                >
                  {t("linkConnectionsPanel.addIntegration")}
                </Button>
              </Box>
            </PermissionGate>
          )}

          {adding && (
            <ItemFormSurface
              mode="create"
              onClose={() => setAdding(false)}
              busy={busy}
              title={t("linkConnectionsPanel.addIntegration")}
              data-testid="link-add-dialog"
              footerTop={
                saveError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {saveError}
                  </Alert>
                )
              }
              actions={(dismiss) => (
                <>
                  <Button
                    color="inherit"
                    disabled={busy}
                    onClick={() => dismiss()}
                  >
                    {t("common:buttons.cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={busy || !chosen}
                    onClick={async () => {
                      if (await save()) dismiss();
                    }}
                    data-testid="link-save"
                  >
                    {t("linkConnectionsPanel.saveIntegration")}
                  </Button>
                </>
              )}
            >
              <Stack spacing={2} sx={{ pt: 1 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="link-vendor-label">
                    {t("linkConnectionsPanel.integration")}
                  </InputLabel>
                  <Select
                    labelId="link-vendor-label"
                    label={t("linkConnectionsPanel.integration")}
                    value={vendor}
                    onChange={(e) => {
                      setVendor(String(e.target.value));
                      setConfig({});
                      setCredentials({});
                    }}
                    data-testid="link-vendor"
                  >
                    {/* No hardcoded list. Every option is an adapter this build
                        can actually talk to. */}
                    {adapters.map((a) => (
                      <MenuItem key={a.vendor} value={a.vendor}>
                        {a.operational
                          ? a.displayName
                          : t("linkConnectionsPanel.vendorNotImplemented", {
                              name: a.displayName,
                            })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {chosen && !chosen.operational && (
                  <Alert severity="warning">
                    {chosen.reason ??
                      t("linkConnectionsPanel.declaredNotImplemented")}
                  </Alert>
                )}

                {(chosen?.configFields ?? []).some(
                  (f) => f.kind === "outlets"
                ) && (
                  <Alert severity="info">
                    {t("linkConnectionsPanel.outletsAfterSave", {
                      name: chosen?.displayName,
                    })}
                  </Alert>
                )}

                {(chosen?.configFields ?? [])
                  .filter((f) => f.kind !== "outlets")
                  .map((f) => (
                    <TextField
                      key={f.key}
                      size="small"
                      label={f.label}
                      placeholder={f.placeholder}
                      helperText={f.help}
                      value={config[f.key] ?? ""}
                      onChange={(e) =>
                        setConfig({ ...config, [f.key]: e.target.value })
                      }
                    />
                  ))}

                {(chosen?.credentialFields ?? []).map((f) => (
                  <TextField
                    key={f.key}
                    size="small"
                    type="password"
                    label={f.label}
                    helperText={
                      f.help ?? t("linkConnectionsPanel.storedEncrypted")
                    }
                    value={credentials[f.key] ?? ""}
                    onChange={(e) =>
                      setCredentials({
                        ...credentials,
                        [f.key]: e.target.value,
                      })
                    }
                  />
                ))}
              </Stack>
            </ItemFormSurface>
          )}

          {/* Said out loud rather than left absent: an operator looking for
              on-prem OPERA must find out here, not from a silent failure. */}
          <Typography variant="caption" color="text.secondary">
            {t("linkConnectionsPanel.operaOnPremise")}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
